import { DataCache } from '.';
import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from '..';
import { eventBus } from '../../internal';
import { IdentityAvatarType, IdentityType } from '../../services';
import { clone } from '../../util';

// IdentitiesCache is an in-memory cache to hold on to user identity information.
// Meant to help application performance by reducing network calls to identities
// service. The cache is invalidated on all known non-Get calls to identities
// service.
// Invalidates itself on 'Cache.Reload' and 'AppSwitcher.AppShow' message on
// the event bus.
export class IdentitiesCache implements DataCache {
  private helper: IdentitiesCacheHelper;

  readonly name: string = 'sas.IdentitiesCache';

  constructor(private _axios: AxiosInstance) {
    this.helper = new IdentitiesCacheHelper(_axios);
    // subscribe to a reload event to trigger cache reload.
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    eventBus.subscribe('Cache.Reload', () => this.helper.invalidate());
    // subscribe to app show event to trigger cache reload.
    eventBus.subscribe('AppSwitcher.AppShow', () => this.helper.invalidate());
  }

  canHandle(conf: AxiosRequestConfig): boolean {
    if (
      !conf.url ||
      conf.method === undefined ||
      (conf.responseType && conf.responseType !== 'json')
    ) {
      return false;
    }
    return SupportedEpRegEx.test(conf.url!);
  }

  handle<T = any, R = AxiosResponse<T>>(conf: AxiosRequestConfig): Promise<R> {
    if (!this.canHandle(conf)) {
      return this._axios(conf) as Promise<any>;
    }

    // invalidate cache on non-Get calls.
    if (conf.method!.toUpperCase() !== 'GET') {
      this.helper.invalidate();
      return this._axios(conf) as Promise<any>;
    }

    const parsedUri = new URL(conf.url!, self.location.origin);

    if (parsedUri.search && conf.params !== undefined) {
      return this._axios(conf) as Promise<any>;
    }

    // check if the call matches exactly with service urls..
    // Verify if accept header is specified and matches with the supported
    // header value.
    // identities endpoints are specific about what they accept.
    if (parsedUri.pathname === SrvUsrsEp && acceptHdr(conf.headers).endsWith('json')) {
      // The Promise.resolve() method returns a Promise object that is resolved
      // with a given value.
      // **If the value is a promise, that promise is returned;..**
      return Promise.resolve<R>((this.helper.getUsrIdentity() as unknown) as R);
    }

    if (parsedUri.pathname === SrvUsrsAvtrEp && acceptHdr(conf.headers).endsWith('json')) {
      return Promise.resolve<R>((this.helper.getUsrAvatar() as unknown) as R);
    }

    // identities endpoints are specific about what they accept. Its sad that
    // sometimes just application/json is not enough for /administrator
    // path. :(
    if (parsedUri.pathname === SrvUsrsAdminEp && acceptHdr(conf.headers) === HdrAcceptAdmn) {
      return Promise.resolve<R>((this.helper.isUsrAdmin() as unknown) as R);
    }

    return this._axios(conf) as Promise<any>;
  }
}

// acceptHdr returns lowercase accept header if specified in input headers object.
const acceptHdr = (headers: any): string => {
  if (headers && ('Accept' in headers || 'accept' in headers)) {
    // Don't bother about mixed case header names as someone is trying to be
    // creative. Let them bypass the cache as its not worth it.
    const accHdr = (headers.accept || headers.Accept) as string;
    return accHdr.toLowerCase();
  }
  return '';
};

// TODO for now, the cache just support endpoints for @currentUser. There is a
// possibility of calls made to identities with actual user name. The cache
// will let the call go on network. Don't see a need to support that use case
// as of now.
const SrvUsrsEp = `/identities/users/@currentUser`;
const SrvUsrsAdminEp = `${SrvUsrsEp}/administrator`;
const SrvUsrsAvtrEp = `${SrvUsrsEp}/avatar`;
const SupportedEpRegEx = new RegExp(`^${SrvUsrsEp}($|/avatar|/administrator)`);
const HdrAcceptIdntt = `application/vnd.sas.identity.user+json`;
const HdrAcceptAdmn = 'application/vnd.sas.primitive.boolean+json';
const HdrAcceptAvtr = 'application/vnd.sas.identity.avatar+json';

class IdentitiesCacheHelper {
  constructor(private _axios: AxiosInstance) {}

  private usrIdnttResp?: AxiosResponse<IdentityType>;
  private usrIdnttPrms?: Promise<AxiosResponse<IdentityType>>;

  async getUsrIdentity(): Promise<AxiosResponse<IdentityType>> {
    if (this.usrIdnttResp === undefined) {
      if (this.usrIdnttPrms === undefined) {
        this.usrIdnttPrms = new Promise<AxiosResponse<IdentityType>>(async (res, reject) => {
          try {
            this.usrIdnttResp = await this._axios.get<IdentityType>(SrvUsrsEp, {
              headers: {
                Accept: HdrAcceptIdntt,
              },
              cache: false,
            });
            res(clone(this.usrIdnttResp));
          } catch (err: any) {
            // clear promise reference to let following calls to retry. Could be
            // a case when very first call returns an error.
            this.usrIdnttPrms = undefined;
            reject(err);
          }
        });
      }
      return this.usrIdnttPrms;
    }
    return clone(this.usrIdnttResp);
  }

  private usrAdmnResp?: AxiosResponse<boolean>;
  private usrAdmnPrms?: Promise<AxiosResponse<boolean>>;

  async isUsrAdmin(): Promise<AxiosResponse<boolean>> {
    if (this.usrAdmnResp === undefined) {
      if (this.usrAdmnPrms === undefined) {
        this.usrAdmnPrms = new Promise<AxiosResponse<boolean>>(async (res, reject) => {
          try {
            this.usrAdmnResp = await this._axios.get<boolean>(SrvUsrsAdminEp, {
              headers: {
                Accept: HdrAcceptAdmn,
              },
              cache: false,
            });
            res(clone(this.usrAdmnResp));
          } catch (err: any) {
            // clear promise reference to let following calls to retry. Could be
            // a case when very first call returns an error.
            this.usrAdmnPrms = undefined;
            reject(err);
          }
        });
      }
      return this.usrAdmnPrms;
    }
    return clone(this.usrAdmnResp);
  }

  private usrAvtrResp?: AxiosResponse<IdentityAvatarType>;
  private usrAvtrPrms?: Promise<AxiosResponse<IdentityAvatarType>>;

  async getUsrAvatar(): Promise<AxiosResponse<IdentityAvatarType>> {
    if (this.usrAvtrResp === undefined) {
      if (this.usrAvtrPrms === undefined) {
        this.usrAvtrPrms = new Promise<AxiosResponse<IdentityAvatarType>>(async (res, reject) => {
          try {
            this.usrAvtrResp = await this._axios.get<IdentityAvatarType>(SrvUsrsAvtrEp, {
              headers: {
                Accept: HdrAcceptAvtr,
              },
              cache: false,
            });
            res(clone(this.usrAvtrResp));
          } catch (err: any) {
            // clear promise reference to let following calls to retry. Could be
            // a case when very first call returns an error.
            this.usrAvtrPrms = undefined;
            reject(err);
          }
        });
      }
      return this.usrAvtrPrms;
    }
    return clone(this.usrAvtrResp);
  }

  invalidate() {
    this.usrAdmnResp = undefined;
    this.usrAvtrResp = undefined;
    this.usrIdnttResp = undefined;
    this.usrIdnttPrms = undefined;
    this.usrAdmnPrms = undefined;
    this.usrAvtrPrms = undefined;
  }
}
